// Section-marker convention for targeted refinement.
//
// Generated pages are asked (see functions/systemPrompt.js) to wrap their
// major top-level content regions in HTML comment markers:
//   <!--LL_SECTION:sim--> ... <!--/LL_SECTION:sim-->
// This lets a refinement request ("make the quiz harder") touch ONE region
// instead of re-sending the whole file to the generation gateway — which is
// what functions/entitlement.js's MAX_CURRENT_HTML_CHARS cap ("Current file
// is too large to send for refinement") otherwise blocks on every single
// refinement of a large page, even when the requested change is tiny and
// localized to one part of it.
//
// Parsing/splicing lives here (client-side) rather than shared with the
// server on purpose: the entire point is to shrink what leaves the browser,
// so the full file must never be sent to the server just to have it do this
// parsing. functions/sections.js keeps only the fixed name vocabulary, for
// server-side validation of the `refineSection` value.
//
// See src/lib/generate.js for how this plugs into the generation request.
// There's no manual "pick a section" UI — src/views/studio.js decides
// automatically: small pages always send the whole file (zero extra steps,
// so a kid with a simple page never sees any of this); only once a page's
// rough size crosses SECTION_MODE_SUGGEST_THRESHOLD does it try to resolve
// the learner's existing comment-pin(s) (see lib/elementPicker.js) to a
// single section via findSectionForLocator/resolvePinsToSection below, and
// fall back to sending the whole file (today's behavior, size limits and
// all) if there's no pin or the pin doesn't resolve cleanly.

export const SECTION_NAMES = ['sim', 'quiz', 'explanation', 'intro'];

export const SECTION_LABELS = {
  sim: 'Simulation & controls',
  quiz: 'Quiz',
  explanation: 'Explanation',
  intro: 'Intro',
};

const openTag = (name) => `<!--LL_SECTION:${name}-->`;
const closeTag = (name) => `<!--/LL_SECTION:${name}-->`;

// Matches one full marker pair with its inner content. Rebuilt on every call
// (rather than a shared module-level RegExp) because it's used with .exec()
// in a loop and a shared `g`-flagged RegExp carries mutable lastIndex state
// that would leak between unrelated calls.
function sectionRegex() {
  return /<!--\s*LL_SECTION:([a-zA-Z0-9_-]+)\s*-->([\s\S]*?)<!--\s*\/LL_SECTION:\1\s*-->/g;
}

/**
 * Finds every <!--LL_SECTION:x--> ... <!--/LL_SECTION:x--> pair in html, in
 * source order. `start`/`end` are the character offsets of the FULL marker
 * pair (open tag through close tag, inclusive) so callers can splice
 * precisely without re-searching.
 * @param {string} html
 * @returns {Array<{name:string, inner:string, start:number, end:number}>}
 */
export function listSections(html) {
  const source = String(html || '');
  const re = sectionRegex();
  const out = [];
  let match;
  while ((match = re.exec(source))) {
    out.push({ name: match[1], inner: match[2], start: match.index, end: match.index + match[0].length });
  }
  return out;
}

export function hasSections(html) {
  return listSections(html).length > 0;
}

/**
 * @returns {string|null} the section's inner content, or null if that named
 * section doesn't exist in html (e.g. an older page generated before this
 * feature, or a page whose content genuinely has no such region).
 */
export function extractSection(html, name) {
  const found = listSections(html).find((s) => s.name === name);
  return found ? found.inner : null;
}

/**
 * Replaces just the inner content of one named section, keeping its marker
 * comments intact. Returns the full, spliced document — or the original
 * document unchanged if that section isn't present (callers should have
 * already checked with extractSection before committing to section mode).
 */
export function replaceSectionContent(html, name, newInner) {
  const source = String(html || '');
  const target = listSections(source).find((s) => s.name === name);
  if (!target) return source;
  const before = source.slice(0, target.start);
  const after = source.slice(target.end);
  return `${before}${openTag(name)}\n${String(newInner || '').trim()}\n${closeTag(name)}${after}`;
}

/**
 * Builds the small "skeleton" sent to the model in place of the whole file:
 * every section's inner content is collapsed to a short placeholder comment,
 * so the model still sees the page's real structure — <head>, shared
 * <style>/<script>, ids/classes other sections reference — without paying
 * for the weight of content it isn't revising this turn. The target
 * section's real content is sent separately (see generate.js) so it's never
 * duplicated in the request.
 */
export function buildSkeleton(html, targetName) {
  const source = String(html || '');
  const sections = listSections(source);
  if (sections.length === 0) return source;
  let out = '';
  let cursor = 0;
  for (const s of sections) {
    out += source.slice(cursor, s.start);
    const note = s.name === targetName
      ? `<!-- current content of this section is sent separately as CURRENT SECTION CONTENT -->`
      : `<!-- section content omitted here (${s.inner.length.toLocaleString()} chars, unchanged for this request) -->`;
    out += `${openTag(s.name)}\n${note}\n${closeTag(s.name)}`;
    cursor = s.end;
  }
  out += source.slice(cursor);
  return out;
}

/**
 * Best-effort recovery of the new section content from whatever the model
 * returned for a section-only refinement. Handles the well-behaved case
 * (a fragment wrapped in the expected markers, found anywhere in the
 * response) and falls back gracefully if the model ignored the fragment-
 * only contract and returned a full document anyway, so a prompt-following
 * slip doesn't corrupt the page by splicing an entire <html> document in as
 * if it were just one section's inner content.
 */
export function coerceSectionFragment(raw, sectionName) {
  const text = String(raw || '');
  const direct = extractSection(text, sectionName);
  if (direct !== null) return direct;
  const bodyMatch = /<body[^>]*>([\s\S]*)<\/body>/i.exec(text);
  if (bodyMatch) return bodyMatch[1].trim();
  return text.trim();
}

/**
 * Resolves a single comment-pin's locator (a best-effort CSS selector path —
 * see lib/elementPicker.js, generated by clicking an element inside the
 * rendered iframe) to the name of the LL_SECTION it falls inside, by
 * parsing `html` and walking it in document order, tracking which section's
 * open/close marker COMMENTS we're currently between. Comments are ordinary
 * sibling nodes to whatever they wrap, so by the time traversal reaches the
 * matched element, the top of `stack` is exactly the section it's nested in
 * — this works regardless of how deep the element sits inside that
 * section's markup, and needs no string-offset bookkeeping.
 *
 * Returns null if `locator` doesn't resolve to anything in `html`, or if the
 * matched element isn't inside any LL_SECTION marker (e.g. the page predates
 * this feature, or the click landed on shared chrome outside every section)
 * — callers should treat null as "can't do a targeted edit here" and fall
 * back to sending the whole file, exactly like before this feature existed.
 */
export function findSectionForLocator(html, locator) {
  if (typeof DOMParser === 'undefined' || !locator) return null;
  let doc;
  try {
    doc = new DOMParser().parseFromString(String(html || ''), 'text/html');
  } catch (e) {
    return null;
  }
  let target = null;
  try {
    target = doc.querySelector(locator);
  } catch (e) {
    return null; // an invalid/stale selector — treat as unresolved, not a crash
  }
  if (!target) return null;

  const OPEN_RE = /^LL_SECTION:([a-zA-Z0-9_-]+)$/;
  const CLOSE_RE = /^\/LL_SECTION:([a-zA-Z0-9_-]+)$/;
  const stack = [];
  let result = null;

  function visit(node) {
    if (result) return;
    if (node.nodeType === 8 /* Node.COMMENT_NODE */) {
      const text = (node.data || '').trim();
      const openMatch = OPEN_RE.exec(text);
      if (openMatch) { stack.push(openMatch[1]); return; }
      const closeMatch = CLOSE_RE.exec(text);
      if (closeMatch && stack[stack.length - 1] === closeMatch[1]) { stack.pop(); }
      return;
    }
    if (node === target) {
      result = stack.length ? stack[stack.length - 1] : null;
      return;
    }
    const children = node.childNodes;
    for (let i = 0; i < children.length; i += 1) {
      visit(children[i]);
      if (result) return;
    }
  }
  visit(doc.documentElement);
  return result;
}

/**
 * Resolves a whole set of comment pins (see studio.js's pendingPins) to ONE
 * target section, refusing (returning null) unless every pin lands inside
 * the SAME section — mixing pins across sections, or any pin that can't be
 * placed at all, isn't safe to turn into a single-section edit, so the
 * caller falls back to sending the whole file rather than guessing.
 */
export function resolvePinsToSection(html, pins) {
  if (!pins || pins.length === 0) return null;
  const names = new Set();
  for (const pin of pins) {
    const name = findSectionForLocator(html, pin.locator);
    if (!name) return null;
    names.add(name);
  }
  return names.size === 1 ? [...names][0] : null;
}
